import { makeStyles } from "@material-ui/core/styles";

// Folder level stylesheet for every appointments screen, following the repo
// convention (see pages/knowledgeBase/styles.js, which Form.js and
// DeleteConfirmation.js share). The Appointments page (task 11.3) and the
// appointment form (task 11.4) should ADD rules here rather than replace the
// file.

// The height of one 30 minute slot row. Shared with AppointmentCalendar.js,
// which positions duration-spanning blocks in slot units and multiplies by this
// (Requirement 9.2), so the blocks and the grid lines can never disagree.
export const SLOT_HEIGHT = 34;

//width of the left hand time gutter holding the 8:00 AM .. 7:30 PM labels
export const TIME_GUTTER_WIDTH = 74;

export default makeStyles(theme => ({
  margin: {
    margin: theme.spacing(0.5),
  },
  calendarToolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  calendarToolbarSpacer: {
    flexGrow: 1,
  },
  calendarRangeLabel: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  calendarMessage: {
    padding: theme.spacing(1.5),
    textAlign: "center",
  },
  //horizontal scroll rather than squeezing seven columns to nothing on a phone
  calendarScroll: {
    overflowX: "auto",
  },
  calendarGrid: {
    minWidth: 620,
  },
  //a single day has one column, so it does not need the seven column min width
  calendarGridDay: {
    minWidth: 260,
  },
  calendarHeaderRow: {
    display: "flex",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  calendarBodyRow: {
    display: "flex",
  },
  timeGutter: {
    flex: `0 0 ${TIME_GUTTER_WIDTH}px`,
    width: TIME_GUTTER_WIDTH,
  },
  timeGutterSlot: {
    height: SLOT_HEIGHT,
    paddingRight: theme.spacing(1),
    textAlign: "right",
    //the label sits at the top of its row, level with the grid line it names
    lineHeight: 1,
    color: theme.palette.text.secondary,
  },
  dayColumnHeader: {
    flex: "1 1 0",
    minWidth: 0,
    padding: theme.spacing(0.5),
    textAlign: "center",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  dayColumnHeaderToday: {
    fontWeight: 600,
  },
  //blocks are positioned against this, so it must stay the positioning context
  dayColumn: {
    position: "relative",
    flex: "1 1 0",
    minWidth: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  slotRow: {
    height: SLOT_HEIGHT,
    boxSizing: "border-box",
    borderBottom: `1px dotted ${theme.palette.divider}`,
  },
  slotRowUnavailable: {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  slotRowHour: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  // Opt-in slot controls retain the grid's geometry while becoming real,
  // keyboard-operable buttons for the create form only.
  slotButton: {
    display: "flex",
    width: "100%",
    padding: 0,
    justifyContent: "flex-start",
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.dark}`,
      outlineOffset: -2,
      zIndex: 1,
    },
  },
  slotButtonUnavailable: {
    // ButtonBase otherwise fades disabled controls enough to make the calendar
    // look like it has failed to render; retain a clearly unavailable row.
    "&.Mui-disabled": {
      opacity: 1,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  slotButtonSelected: {
    backgroundColor: theme.palette.primary.light,
    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.dark}`,
  },
  slotSelectionStatus: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.light,
  },
  // The appointments of one day, layered over the (decorative) slot rows. It is
  // a real list so a screen reader announces "N appointments" per day instead of
  // walking a pile of positioned divs.
  appointmentLayer: {
    position: "absolute",
    top: 0,
    left: 2,
    right: 2,
    bottom: 0,
    margin: 0,
    padding: 0,
    listStyle: "none",
    //the slot rows underneath must stay hoverable/selectable
    pointerEvents: "none",
  },
  appointmentBlockItem: {
    position: "absolute",
    pointerEvents: "auto",
  },
  appointmentBlockFill: {
    width: "100%",
    height: "100%",
  },
  appointmentBlockContent: {
    display: "block",
    width: "100%",
    fontSize: "0.7rem",
  },
  appointmentBlock: {
    overflow: "hidden",
    display: "block",
    textAlign: "left",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: "2px 4px",
    borderRadius: 3,
    borderLeft: "4px solid",
    borderLeftColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.text.primary,
    //guarded: this shared stylesheet is evaluated in full by whichever
    //appointments component mounts first, so a theme without the app's
    //customShadows extension (a non-standard MUI key) must not crash the page
    boxShadow: (theme.customShadows && theme.customShadows.widget) || theme.shadows[2],
  },
  appointmentBlockClickable: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.info.main,
    },
    //never remove the focus ring: these are keyboard reachable
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.dark}`,
      outlineOffset: -2,
    },
  },
  appointmentBlockCancelled: {
    backgroundColor: theme.palette.background.default,
    borderLeftColor: theme.palette.text.hint,
    //status is also spelled out in text, so this is decoration only
    textDecoration: "line-through",
  },
  appointmentBlockText: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.25,
  },
  //month view cells
  monthWeekRow: {
    display: "flex",
  },
  monthDayCell: {
    flex: "1 1 0",
    minWidth: 0,
    minHeight: 96,
    padding: theme.spacing(0.5),
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  monthDayCellOutside: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.hint,
  },
  monthDayNumber: {
    display: "block",
    marginBottom: 2,
  },
  monthEntryList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  monthEntry: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "1px 3px",
    marginBottom: 2,
    borderRadius: 3,
    borderLeft: "3px solid",
    borderLeftColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.info.light,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  outsideGridList: {
    margin: 0,
    padding: theme.spacing(0.5),
    listStyle: "none",
  },
  //--- AppointmentForm (task 11.4) -----------------------------------------
  //the fields column; the handler calendar sits beside it on md and up and
  //stacks underneath on a phone (plain Grid breakpoints, no media queries here)
  formSection: {
    paddingRight: theme.spacing(1),
  },
  formSidebar: {
    paddingLeft: theme.spacing(1),
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 0,
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(1),
    },
  },
  //shown until a handler is picked, in place of an empty week that would look
  //like "this handler is free all week"
  formSidebarMessage: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  // Requirements 1.5 / 1.6 / 1.7 - the block that names what is wrong. Bordered
  // AND prefixed in words, never colour alone, and it is a role="alert" region so
  // a screen reader hears it the moment a submit is rejected.
  formErrorSummary: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderLeft: "4px solid",
    borderLeftColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.background.light,
  },
  formErrorList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
  formActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  //--- cancelled appointments (task 13.1) ----------------------------------
  // Requirements 13.2 / 13.5 - the banner on a cancelled appointment. Same
  // shape as formErrorSummary but in the error colour, and like it the state is
  // ALSO stated in words inside, never conveyed by the colour alone.
  formCancelledNotice: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderLeft: "4px solid",
    borderLeftColor: theme.palette.error.main,
    backgroundColor: theme.palette.background.light,
  },
  //the confirmation dialog's content (CancelConfirmation.js); the dialog itself
  //supplies the chrome, so this is only the body
  cancelConfirmation: {
    textAlign: "left",
  },
  cancelConfirmationSummary: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.light,
  },
  cancelConfirmationError: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    borderLeft: "4px solid",
    borderLeftColor: theme.palette.error.main,
    backgroundColor: theme.palette.background.light,
  },
  cancelConfirmationActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  //--- Appointments list page (task 11.3) -----------------------------------
  filterHeader: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  filterViewToggle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // The filter row wraps instead of scrolling: on a phone each control simply
  // drops to the next line rather than being squeezed below a usable width.
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
  },
  filterField: {
    flex: "1 1 170px",
    minWidth: 150,
    maxWidth: 240,
    marginRight: theme.spacing(1),
  },
  filterGroupLabel: {
    display: "block",
    whiteSpace: "nowrap",
  },
  filterActions: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(1),
  },
  //said out loud when the date range filter and the range on screen do not meet,
  //so an empty calendar is never left unexplained (Requirement 10.6)
  filterNotice: {
    flexBasis: "100%",
    marginTop: theme.spacing(1),
  },
  // Requirement 10.5 - the detail/edit panel. It is a focusable region rather
  // than a modal: focus is moved here when it opens, Escape closes it, and the
  // calendar underneath stays readable.
  detailPanel: {
    marginBottom: theme.spacing(2),
    //the focus ring stays: this element is what receives focus on open
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.dark}`,
      outlineOffset: 2,
    },
  },
  detailPanelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  //--- Jewish holidays ------------------------------------------------------
  // Two levels, and they mean different things:
  //
  //   YOM TOV     - work is not permitted, so the day is BLOCKED OUT. Styled like
  //                 the unavailable slot rows above so "not selectable" reads the
  //                 same way it already does elsewhere in the grid.
  //   CHOL HAMOED - bookable, but worth noticing, so a subtle tint plus the label.
  //
  // Colour is never the only signal: the holiday NAME is always rendered as text,
  // and a blocked day additionally carries a tooltip and aria-disabled saying why.
  holidayLabel: {
    display: "block",
    //week columns are narrow, so a long name truncates and the tooltip carries it
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.65rem",
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
  },
  holidayLabelYomTov: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  holidayLabelCholHaMoed: {
    fontWeight: 600,
  },
  //blocked out: the same treatment unavailable slots already use
  dayYomTov: {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  // Chol HaMoed: present but quiet. `fade` is avoided so this keeps working on a
  // theme without it; a plain low-opacity overlay colour is enough.
  dayCholHaMoed: {
    backgroundColor: theme.palette.background.light,
    boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.light}`,
  },
}));
